import { useTheme } from '@/context/ThemeContext';

const cases = [
  {
    metric: '85%',
    metricLabel: 'Reduction in Missed Calls',
    secondary: '+40% Booked Jobs',
    desc: 'Reduced missed calls by 85% and increased booked jobs by 40% for a home service company.',
    industry: 'Home Services',
    icon: 'ri-tools-line',
    img: 'https://public.readdy.ai/ai/img_res/13a11c013531ecd8ce6770c9470ebaef.jpg',
  },
  {
    metric: '+30%',
    metricLabel: 'More Consultations',
    secondary: 'Automated Intake',
    desc: 'Automated intake increased consultation bookings for a law firm by 30% within the first 60 days.',
    industry: 'Law Firm',
    icon: 'ri-scales-line',
    img: 'https://readdy.ai/api/search-image?query=attorney%20in%20modern%20law%20office%20reviewing%20documents%2C%20professional%20setting%2C%20dramatic%20window%20light%2C%20dark%20wood%20furniture%2C%20confident%20professional%2C%20cinematic%20photography%2C%20high%20contrast&width=600&height=400&seq=case-law1&orientation=landscape',
  },
  {
    metric: '2s',
    metricLabel: 'Average Response Time',
    secondary: 'Was: Hours',
    desc: 'Improved response time from hours to seconds for a property management team handling 200+ units.',
    industry: 'Property Management',
    icon: 'ri-building-line',
    img: 'https://readdy.ai/api/search-image?query=modern%20apartment%20complex%20aerial%20view%2C%20urban%20real%20estate%2C%20clean%20architecture%2C%20blue%20sky%2C%20professional%20real%20estate%20photography%2C%20sharp%20details%2C%20contemporary%20building%20design&width=600&height=400&seq=case-prop1&orientation=landscape',
  },
];

export default function CaseStudiesSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section
      id="case-studies"
      className={`relative py-28 md:py-36 overflow-hidden transition-colors duration-500 ${isDark ? 'bg-black' : 'bg-[#F5F5F7]'}`}
    >
      <div className={`absolute top-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-[#38D6D6]/20 to-transparent' : 'bg-gradient-to-r from-transparent via-[#38D6D6]/30 to-transparent'}`} />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-px bg-[#38D6D6]/60"></div>
          <span className="text-xs font-grotesk text-[#38D6D6] tracking-widest uppercase">Results</span>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <h2 className={`font-sora font-800 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>
            Real<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
              Operational Impact
            </span>
          </h2>
          <p className={`font-grotesk text-base max-w-sm leading-relaxed ${isDark ? 'text-white/45' : 'text-black/45'}`}>
            Measurable results across industries. These are real outcomes from deployed systems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cases.map((c, i) => (
            <div
              key={i}
              className={`group relative rounded-2xl border overflow-hidden transition-all duration-300 hover:-translate-y-1.5 ${
                isDark ? 'border-white/8 bg-[#0D1117]' : 'border-black/8 bg-white'
              } ${i === 1 ? 'md:-translate-y-4' : ''}`}
            >
              {/* Image */}
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src={c.img}
                  alt={c.industry}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70"></div>
                {/* Industry tag */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 rounded-full text-xs font-grotesk font-500 bg-black/50 backdrop-blur-sm text-white/80 border border-white/15">
                    {c.industry}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#38D6D6]/10 border border-[#38D6D6]/20">
                    <i className={`${c.icon} text-[#38D6D6] text-sm`}></i>
                  </div>
                  <span className={`text-xs font-grotesk ${isDark ? 'text-white/35' : 'text-black/35'}`}>{c.secondary}</span>
                </div>

                <div className="mb-1">
                  <span className="font-sora font-800 text-5xl text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
                    {c.metric}
                  </span>
                </div>
                <div className={`font-grotesk text-sm mb-4 ${isDark ? 'text-white/50' : 'text-black/50'}`}>{c.metricLabel}</div>

                <p className={`font-grotesk text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-black/60'}`}>{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={`absolute bottom-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-white/5 to-transparent' : 'bg-gradient-to-r from-transparent via-black/5 to-transparent'}`} />
    </section>
  );
}
